import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import type { Channel } from 'pusher-js';

import { useDisclosure } from '@mantine/hooks';

import { BADGE_VARIANTS } from 'constants/mantineVariants';
import {
  PUBLISH_MENU_VALIDATION_SUCCESSFUL,
  PUBLISH_MENU_VALIDATION_FAILED,
  MENU_OBJECT_TYPES,
} from 'constants/menuManagement';

import { replaceTypeWithReadableInString, validateMenu } from 'helpers/menuManagement';

import { Accordion, Badge, Button, Flex, Group, Stack, Text } from '@mantine/core';

import { FixImageUrlsModal } from 'components/Modals/FixImageUrlsModal/FixImageUrlsModal';
import { useMenuManagement } from 'hooks/useMenuManagement';

import styles from 'styles/theme.module.scss';

import { BellIconMd, WarningIconMd } from '@chownow/pantry/icons';
import type { Menu as SharedMenu } from '../types';

const ALCOHOLIC_ERROR_MESSAGE =
  'This menu contains alcoholic item(s)/modifier(s), for more details please check your menu.';

// PublishMenuModal coerces `uuid` to a string before passing it to this step,
// so we narrow the shared type here rather than re-declaring the shape.
type Menu = Omit<SharedMenu, 'uuid'> & { uuid: string };

type ValidationResultMessage = {
  result: 'pass' | 'warning' | string;
  message: string;
};

type ValidationPayload = {
  menuUuid: string;
  menuIsValid: boolean;
  resultMessages: ValidationResultMessage[];
  hasInvalidUrls?: boolean;
  menuHasAlcohol?: boolean;
};

type ValidationStepProps = {
  channels: Channel[] | null;
  gap: string;
  menu: Menu;
  nextStep: () => void;
  isFetchingNextStepData: boolean;
  handleDoneClick: () => void;
  showStorefrontError: boolean;
  integrationsIncludesStorefront: boolean;
};

export const ValidationStep: FunctionComponent<ValidationStepProps> = ({
  channels,
  gap,
  menu,
  nextStep,
  isFetchingNextStepData,
  handleDoneClick,
  showStorefrontError,
  integrationsIncludesStorefront,
}) => {
  const { updateLocalState } = useMenuManagement();
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationPayload | null>(null);
  const [isFixImageUrlsModalOpen, { open: openFixImageUrlsModal, close: closeFixImageUrlsModal }] =
    useDisclosure();

  const { merchantId, companyId, uuid: menuId } = menu;

  const publishPreviouslyValidated = menu.menuIsValidated && !validationResult?.menuUuid;
  const publishResults = validationResult?.menuUuid;
  const publishNeedsValidation = !publishPreviouslyValidated && !publishResults;

  const hasInvalidResults = Boolean(validationResult?.menuUuid) && !validationResult?.menuIsValid;

  const hasNonBlockingWarnings =
    validationResult?.menuIsValid &&
    (validationResult?.resultMessages?.some(r => r.result === 'warning') ||
      validationResult?.menuHasAlcohol ||
      validationResult?.hasInvalidUrls);

  let summaryBadgeVariant: string = BADGE_VARIANTS.success;
  let summaryBadgeLabel = 'Pass';
  if (hasInvalidResults) {
    summaryBadgeVariant = BADGE_VARIANTS.caution;
    summaryBadgeLabel = 'Fail';
  } else if (hasNonBlockingWarnings) {
    summaryBadgeVariant = BADGE_VARIANTS.yellow;
    summaryBadgeLabel = 'Warning';
  }

  let primaryButtonLabel = 'Validate';
  if (hasInvalidResults) {
    primaryButtonLabel = 'Done';
  } else if (!publishNeedsValidation) {
    primaryButtonLabel = 'Next';
  }

  const channel =
    companyId &&
    channels?.find(
      (ch: Channel) => ch.name === `${import.meta.env.VITE_WEB_SOCKET_STAGE}-company-${companyId}`,
    );

  useEffect(() => {
    if (
      validationResult?.menuUuid &&
      (menu?.menuIsValidated !== validationResult?.menuIsValid ||
        validationResult?.hasInvalidUrls !== menu?.hasInvalidUrls)
    ) {
      updateLocalState({
        group: MENU_OBJECT_TYPES.menus,
        id: menu?.uuid,
        data: {
          ...menu,
          menuIsValidated: validationResult?.menuIsValid,
          hasInvalidUrls: validationResult?.hasInvalidUrls,
        },
      });
    }
  }, [menu, updateLocalState, validationResult]);

  useEffect(() => {
    if (!channel) return undefined;
    channel.bind(PUBLISH_MENU_VALIDATION_SUCCESSFUL, (payload: ValidationPayload) => {
      if (payload.menuUuid === menuId) {
        setValidationResult(payload);
        setIsValidating(false);
      }
    });
    channel.bind(PUBLISH_MENU_VALIDATION_FAILED, (payload: ValidationPayload) => {
      if (payload.menuUuid === menuId) {
        setIsValidating(false);
      }
    });
    return () => {
      channel.unbind(PUBLISH_MENU_VALIDATION_SUCCESSFUL);
      channel.unbind(PUBLISH_MENU_VALIDATION_FAILED);
    };
  }, [channel, menuId]);

  const handleValidate = async () => {
    if (!channel) return;

    setValidationResult(null);
    setIsValidating(true);
    try {
      await validateMenu({
        menuId,
        companyId,
        merchantId,
        isPublish: true,
      });
    } catch (e) {
      setIsValidating(false);
    }
  };

  const handleFixImageUrlsClick = openFixImageUrlsModal;
  const handleFixImageUrlsModalClose = closeFixImageUrlsModal;

  function getResults() {
    if (!validationResult?.menuUuid) return '';

    const results: React.ReactNode[] = validationResult.resultMessages.map(result => {
      if (result.result === 'pass') return '';
      return (
        <Group gap="xs2" wrap="nowrap" align="flex-start" key={result.message}>
          <Text miw="md" style={{ display: 'flex', alignItems: 'flex-start' }}>
            {result.result === 'fail' ? <WarningIconMd /> : <BellIconMd />}
          </Text>
          <Text>{replaceTypeWithReadableInString(result.message).split('%-%')[0]}</Text>
        </Group>
      );
    });

    if (validationResult.menuHasAlcohol) {
      results.push(
        <Group gap="xs2" wrap="nowrap" align="flex-start" key="alcoholic-warning">
          <Text miw="md" style={{ display: 'flex', alignItems: 'flex-start' }}>
            {integrationsIncludesStorefront ? <WarningIconMd /> : <BellIconMd />}
          </Text>
          <Text>{ALCOHOLIC_ERROR_MESSAGE}</Text>
        </Group>,
      );
    }

    return results;
  }

  return (
    <Stack gap={gap}>
      <Accordion defaultValue="validation" variant="bordered-card">
        <Accordion.Item value="validation">
          <Accordion.Control>
            <Text variant="title-sm">Validation Results</Text>
          </Accordion.Control>
          <Accordion.Panel>
            {publishPreviouslyValidated && (
              <Group gap="xs2">
                <Text>This menu has previously been validated. Please click Next to continue.</Text>
              </Group>
            )}
            {publishResults && (
              <Stack>
                <Badge variant={summaryBadgeVariant} className={styles.publishMenuStatusBadge}>
                  {summaryBadgeLabel}
                </Badge>
                {getResults()}
              </Stack>
            )}
            {publishNeedsValidation && (
              <Group gap="xs2" wrap="nowrap" align="flex-start">
                <Text>
                  Menu must be validated before publication. Validation may take a few minutes to
                  complete.
                </Text>
              </Group>
            )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Flex gap="xs" direction={{ sm: 'column-reverse', md: 'row' }}>
        {publishPreviouslyValidated && (
          <Button
            variant="outline"
            disabled={showStorefrontError || isValidating}
            onClick={() => {
              void handleValidate();
            }}
            fullWidth
          >
            Re-validate
          </Button>
        )}

        {publishResults && validationResult?.hasInvalidUrls && (
          <Button
            variant="outline"
            disabled={showStorefrontError || isValidating}
            onClick={handleFixImageUrlsClick}
            fullWidth
          >
            Fix Image URLs
          </Button>
        )}

        <FixImageUrlsModal
          opened={isFixImageUrlsModalOpen}
          menu={menu}
          onClose={handleFixImageUrlsModalClose}
        />

        <Button
          loading={isValidating || isFetchingNextStepData}
          disabled={!hasInvalidResults && showStorefrontError}
          onClick={() => {
            if (hasInvalidResults) {
              handleDoneClick();
            } else if (publishNeedsValidation) {
              void handleValidate();
            } else {
              nextStep();
            }
          }}
          fullWidth
        >
          {primaryButtonLabel}
        </Button>
      </Flex>
    </Stack>
  );
};
